import { TFunction } from 'react-i18next';
import { array, boolean, object, string } from 'yup';
import { UseMutateFunction } from '@tanstack/react-query';

import { convertUTCToDateBarFormat } from 'utils/utils';
import { EditableRow } from 'features/PunchupPages/CompedTickets/SendCompedTicketsModal';
import {
	Available,
	FormData,
	FormatDateProps,
	NestedTicketObject,
	NumberObject,
	RecipientProps,
	SendTicketsFunctionProps,
} from './types';

/**
 * Builds the localised "shared pool capacity exceeded" toast message from the over-budget pools (only the
 * pool `name`s are read). Shared by the single-recipient `onSubmit` gate and the bulk `onSend` gate so both
 * surfaces speak with one voice. Actionable (tells the manager to reduce the sharing tiers — the pool cap
 * is the non-obvious constraint) and singular/plural-aware.
 */
export const poolExceededMessage = (
	exceededPools: { name: string }[],
	t: TFunction<'translation', undefined>
): string => {
	const names = exceededPools.map((p) => p.name).join(', ');
	if (exceededPools.length === 1) {
		return `${t('send-tickets-pool-exceeded-one', 'Not enough shared capacity in the')} ${names} ${t(
			'send-tickets-pool-exceeded-one-suffix',
			'pool — reduce the tiers that share it before sending.'
		)}`;
	}
	return `${t('send-tickets-pool-exceeded-many', 'Not enough shared capacity in these pools')}: ${names} — ${t(
		'send-tickets-pool-exceeded-many-suffix',
		'reduce the tiers that share them before sending.'
	)}`;
};

/**
 * Bulk-send pre-flight check (the page's `onSend` gate). Returns the first
 * blocking error message — already localised — in the documented priority order
 * (SendTickets/CLAUDE.md → "Bulk-CSV validation state machine", Layer 2), or
 * `null` when the rows are ready to send. `exceededTypes` is the result of
 * `getExceededTicketTypes()`; only the type `name`s are read. `exceededPools` is the
 * result of `getExceededPools()` (lowest priority — checked after per-type capacity);
 * defaults to empty so non-pooled events and existing callers are unaffected.
 */
export const validateBulkBeforeSend = (
	rows: EditableRow[],
	exceededTypes: { name: string }[],
	t: TFunction<'translation', undefined>,
	exceededPools: { name: string }[] = []
): string | null => {
	if (rows.length === 0) {
		return t('send-tickets-bulk-no-rows', 'Upload a file or add rows before sending.');
	}
	const blankCount = rows.filter((r) => r.email.trim() === '').length;
	if (blankCount > 0) {
		return `${t('send-tickets-bulk-missing-email', 'Fix missing email addresses before sending')} (${blankCount} ${
			blankCount === 1 ? 'row' : 'rows'
		}).`;
	}
	const untypedCount = rows.filter((r) => r.ticketTypeId === null).length;
	if (untypedCount > 0) {
		return `${t('send-tickets-bulk-no-type', 'Select a ticket type for all rows before sending')} (${untypedCount} ${
			untypedCount === 1 ? 'row' : 'rows'
		} missing).`;
	}
	const zeroQtyCount = rows.filter((r) => r.quantity === 0).length;
	if (zeroQtyCount > 0) {
		return `${t('send-tickets-bulk-zero-qty', 'Set a quantity for all rows before sending')} (${zeroQtyCount} ${
			zeroQtyCount === 1 ? 'row' : 'rows'
		} with 0).`;
	}
	if (exceededTypes.length > 0) {
		const typeNames = exceededTypes.map((tt) => tt.name).join(', ');
		return `${t('send-tickets-bulk-exceeded', 'Requested quantity exceeds available tickets for')}: ${typeNames}.`;
	}
	if (exceededPools.length > 0) {
		return poolExceededMessage(exceededPools, t);
	}
	return null;
};

export const initialRecipient = {
	firstName: '',
	lastName: '',
	email: '',
	noEmail: false,
	ticketTypes: [],
};

export const initialFormData: FormData = {
	recipients: [initialRecipient],
};

export const formatDate = ({ startDate, timezone, endDate }: FormatDateProps) => ({
	day: convertUTCToDateBarFormat(startDate, timezone, 'MMMM D, YYYY').date,
	time: `${convertUTCToDateBarFormat(startDate, timezone).time} - ${convertUTCToDateBarFormat(endDate, timezone).time}`,
});

export function convertObject(originalObject: NestedTicketObject): NumberObject {
	return Object.keys(originalObject).reduce(
		(newObj, key) => ({ ...newObj, [key]: originalObject[key].quantity }),
		{} as NumberObject
	);
}

export const validationSchema = (t: TFunction<'translation', undefined>) =>
	object().shape({
		recipients: array().of(
			object().shape({
				noEmail: boolean(),
				email: string().when('noEmail', {
					is: true,
					then: (schema) => schema.notRequired(),
					otherwise: (schema) =>
						schema.required(t('createAccount-requiredEmail')).email(t('createAccount-invalidEmail')),
				}),
				firstName: string(),
				lastName: string(),
			})
		),
	});

export const handleSubmit = (
	data: SendTicketsFunctionProps,
	mutateCheckAvailability: UseMutateFunction<Available | undefined, unknown, NestedTicketObject, unknown>
) => {
	const nestedTickets: NestedTicketObject = {};
	data.recipients.forEach((recipient) => {
		recipient.ticketTypes.forEach((ticket) => {
			nestedTickets[ticket.ticket_type_id || 0] = { quantity: ticket.quantity, promocode_id: '' };
		});
	});
	mutateCheckAvailability(nestedTickets);
};

/** Sums `quantity × initialPrice` across every recipient's selected ticket types. */
export function calculateTotalPrice(recipients: RecipientProps[]): number {
	return recipients.reduce((total, recipient) => {
		const ticketTotal = recipient.ticketTypes.reduce((sub, tt) => sub + tt.quantity * tt.initialPrice, 0);
		return total + ticketTotal;
	}, 0);
}

/**
 * Seated-event submit. The seated form is single-recipient (`recipients[0]`), and the
 * form-effect inside `PunchupSendTicketsFormSeated` already groups the chart selection by
 * `ticket_type_id` into one entry per distinct category before submit. So the wire payload
 * is just a flat map of that one recipient's `ticketTypes`. No cross-recipient aggregation
 * is needed — there is only one recipient.
 *
 * The non-seated `handleSubmit` is preserved unchanged because changing its semantics could
 * mask or unmask pre-existing behaviour for non-seated events that depend on the legacy
 * "last recipient wins" aggregation across multiple recipients.
 */
export const handleSubmitSeated = (
	data: SendTicketsFunctionProps,
	mutateCheckAvailability: UseMutateFunction<Available | undefined, unknown, NestedTicketObject, unknown>
) => {
	const recipient = data.recipients[0];
	const nestedTickets: NestedTicketObject = {};
	recipient?.ticketTypes.forEach((ticket) => {
		nestedTickets[ticket.ticket_type_id || 0] = {
			quantity: ticket.quantity,
			promocode_id: '',
		};
	});
	mutateCheckAvailability(nestedTickets);
};
